/**
 * Constants for EntitlementReports feature
 */

export const ENT_REPORT_TABS = {
  RISK_DETAILS: 'INHERENT_ENTITLEMENT_RISK',
  USER_DETAILS: 'INHERENT_ENTITLEMENT_USER',
} as const;

export const TOGGLE_BTNS = [
  {
    id: 0,
    label: 'Risk Details',
    value: ENT_REPORT_TABS.RISK_DETAILS,
  },
  {
    id: 1,
    label: 'User Details',
    value: ENT_REPORT_TABS.USER_DETAILS,
  },
];

export const RISK_DETAILS_PRIORITY_COLUMNS = [
  { value: 'assignedEntitlementDisplayName', label: 'Entitlement' },
  { value: 'assignedEntitlementType', label: 'Entitlement Type' },
  { value: 'riskName', label: 'Risk Name' },
  { value: 'riskDescription', label: 'Risk Description' },
  { value: 'businessProcessNames', label: 'Business Process' },
  { value: 'riskPriority', label: 'Risk Priority' },
  { value: 'riskType', label: 'Risk Type' },
  { value: 'functionName', label: 'Function Name' },
  { value: 'violatingEntitlementDisplayName', label: 'Violating Entitlement' },
  { value: 'violatingEntitlementType', label: 'Violating Entitlement Type' },
  { value: 'violatingObjectName', label: 'Violating Object Name' },
  { value: 'violatingFieldName', label: 'Violating Field Name' },
  { value: 'violatingFieldValue', label: 'Violating Field Value' },
  { value: 'endpointDisplayName', label: 'Application' },
  { value: 'rulesetName', label: 'Ruleset' },
];

export const USER_DETAILS_PRIORITY_COLUMNS = [
  { value: 'assignedEntitlementDisplayName', label: 'Entitlement' },
  { value: 'assignedEntitlementType', label: 'Entitlement Type' },
  { value: 'assignedEntitlementDescription', label: 'Entitlement Description' },
  { value: 'userName', label: 'Username' },
  { value: 'userFirstName', label: 'First Name' },
  { value: 'userLastName', label: 'Last Name' },
  { value: 'userStatus', label: 'User Status' },
  { value: 'email', label: 'User Email' },
];

export const SORT_OPTIONS = [
  {
    label: 'Ascending',
    value: 'asc',
    isDisabled: false,
    isChecked: false,
    icon: 'ArrowDown',
    shouldShowCheckIcon: true,
  },
  {
    label: 'Descending',
    value: 'desc',
    isDisabled: false,
    isChecked: false,
    icon: 'ArrowUp',
    shouldShowCheckIcon: true,
  },
];

// Filter configurations
export const SEVERITY_OPTIONS = [
  { label: 'High', value: 'HIGH', isChecked: false },
  { label: 'Low', value: 'LOW', isChecked: false },
  { label: 'Medium', value: 'MEDIUM', isChecked: false },
  { label: 'Very High', value: 'VERY_HIGH', isChecked: false },
];

export const RISK_DETAILS_FILTERS = [
  {
    label: 'Application',
    value: 'endpointName',
    method: 'api_call_with_search',
    isChecked: true,
    category: 'Application',
    options: [],
  },
  {
    label: 'Risk Name',
    value: 'riskName',
    method: 'api_call_with_search',
    isChecked: true,
    category: 'Risk',
    options: [],
  },
  {
    label: 'Risk Type',
    value: 'riskType',
    method: 'FIXED',
    isChecked: true,
    category: 'Risk',
    options: [
      { label: 'SOD', value: 'SOD', isChecked: false },
      { label: 'Critical Access', value: 'CRITICAL_ACCESS', isChecked: false },
      { label: 'None', value: 'NONE', isChecked: false },
    ],
  },
  {
    label: 'Risk Priority',
    value: 'riskPriority',
    method: 'FIXED',
    isChecked: true,
    category: 'Risk',
    options: [
      { label: 'Critical', value: 'CRITICAL', isChecked: false },
      { label: 'High', value: 'HIGH', isChecked: false },
      { label: 'Medium', value: 'MEDIUM', isChecked: false },
      { label: 'Low', value: 'LOW', isChecked: false },
    ],
  },
  {
    label: 'Entitlement Value',
    value: 'entitlementValue',
    method: 'api_call_with_search',
    isChecked: true,
    category: 'Entitlement',
    options: [],
  },
  {
    label: 'Entitlement Type',
    value: 'entitlementTypeName',
    method: 'api_call_with_search',
    isChecked: true,
    category: 'Entitlement',
    options: [],
  },
  {
    label: 'Sox Critical',
    value: 'entitlementSoxSeverity',
    method: 'FIXED',
    isChecked: true,
    category: 'Entitlement',
    options: SEVERITY_OPTIONS,
  },
  {
    label: 'Sys Critical',
    value: 'entitlementSysSeverity',
    method: 'FIXED',
    isChecked: true,
    category: 'Entitlement',
    options: SEVERITY_OPTIONS,
  },
  {
    label: 'Privileged',
    value: 'entitlementPrivilegeSeverity',
    method: 'FIXED',
    isChecked: true,
    category: 'Entitlement',
    options: SEVERITY_OPTIONS,
  },
];

export const USER_DETAILS_FILTERS = [
  {
    label: 'Application',
    value: 'endpointName',
    method: 'api_call_with_search',
    isChecked: true,
    category: 'Application',
    options: [],
  },
  {
    label: 'Entitlement Type',
    value: 'entitlementTypeName',
    method: 'api_call_with_search',
    isChecked: true,
    category: 'Entitlement',
    options: [],
  },
  {
    label: 'Entitlement Value',
    value: 'entitlementValue',
    method: 'api_call_with_search',
    isChecked: true,
    category: 'Entitlement',
    options: [],
  },
];

export const FILTER_CONFIGS = {
  [ENT_REPORT_TABS.RISK_DETAILS]: RISK_DETAILS_FILTERS,
  [ENT_REPORT_TABS.USER_DETAILS]: USER_DETAILS_FILTERS,
};

// EntReportPreview constants
export const USER_DETAILS_TAB = 'USER_DETAILS';
export const RISK_DETAILS_TAB_INDEX = 1;
export const USER_DETAILS_TAB_INDEX = 2;
export const SEARCH_DEBOUNCE = 400;
export const SELECTION_KEY = 'entitmentsWithInherentRisks';

// Table Constants
export const COLUMN_WIDTH = '12.25rem';
export const EXCLUDED_KEY = 'rowId';
export const PINNED_COLUMN = 'assignedEntitlementDisplayName';

export const ACTION_COLUMN = {
  key: 'actionColumn',
  label: 'actionColumn',
  value: 'actionColumn',
  isSortable: false,
  isPinned: true,
  isColumnConfigEnabled: true,
  isDisabled: true,
  isChecked: true,
  width: '3rem',
};

// Export/Report Modal Constants
export const ENT_REPORT_TYPES = {
  ENTITLEMENTS: 'entitlements',
  ACCESS_REVIEW: 'access_review',
  COMPLIANCE: 'compliance',
} as const;

export const EXPORT_FORMATS = {
  CSV: 'csv',
  XLSX: 'xlsx',
  PDF: 'pdf',
} as const;

export const EXPORT_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  FAILED: 'failed',
} as const;

export const REPORT_MODAL_TABS = {
  CURRENT_VIEW: 'current_view',
  MY_REPORTS: 'my_reports',
  TEMPLATES: 'templates',
} as const;

export const TEMPLATES_TYPES = [
  {
    id: 'entitmentsWithInherentRisks',
    name: 'radioGroupKinds',
    title: 'Entitlements with Inherent Risks',
    description:
      'A detailed report of entitlements, including violating entitlements and risks, users, and objects.',
    value: 'entitmentsWithInherentRisks',
    isChecked: true,
  },
];
